import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CoreCacheService, CoreUserProfileService, PingService, UserProfile } from "@ftr/owa-core";
import {
  JobsService,
  JobTechService,
  lookupEnum,
  JobStatus, ProfileStatus, TechSchedule, ScheduleByDate,
  Job,
  IntrusivePromptDialog
} from '@ftr/owa-jobs';
import { catchError, combineLatestWith, of, Subscription, take } from "rxjs";
import { add, format, differenceInMinutes } from "date-fns";
import { PucNotificationDialog } from '@ftr/owa-jobs';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { NgClass, DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

@Component({
    selector: 'app-tech-information',
    templateUrl: './tech-information.component.html',
    styleUrls: ['./tech-information.component.scss'],
    imports: [NgClass, MatIconModule, DatePipe]
})
export class TechInformationComponent implements OnInit, OnDestroy {
  private _techService = inject(JobTechService);
  private _coreProfileService = inject(CoreUserProfileService);
  private _pingService = inject(PingService);
  private _jobsService = inject(JobsService);
  private _dialog = inject(MatDialog);
  private _coreCacheService = inject(CoreCacheService);


  profileStatus: ProfileStatus | null = null;
  userProfile: UserProfile | null = null;
  currentDate: Date = new Date();
  online: boolean = false;
  isShadowingUser = false;
  jobsLeft: string = '';
  loading = true;
  subscriptions?: Subscription;
  endingScheduleDate: Date | undefined;
  isScheduleDue = false;
  isScheduleDueIn30Minutes = false;
  todaysDate = format(new Date(), "yyyy-MM-dd");
  tomorrowsDate = format(add(new Date(), {
    days: 1
  }), "yyyy-MM-dd");
  finishTime = 0;
  intervalToCheckSchedule: any;
  private shownJobIds: Set<string> = new Set();
  jobDisplayStatus:boolean=false;

  ngOnInit(): void {
    const canAccessJobs: boolean = this._coreProfileService.checkAuthorization('JOB', 'CanAccessJobs');
    let canAccessShadowInfo = this._coreProfileService.checkAuthPermisions({name:"TEC", authorizations:["CanAccessOthersShadowInfo","CanAccessShadowService"],requiredAll:false});
    const connectedSourceChanged$ = this._pingService.connectedSourceChanged$.pipe(catchError(_ => of(null)));
    const profileStatus$ = this._techService.profileStatus.pipe(catchError(_ => of(null)));
    const shadowInfo$ = canAccessShadowInfo ? this._techService.getShadowInfo().pipe(catchError(_ => of(null))):of(null);
    const techSchedule$ = this._techService.getTechSchedule(this.todaysDate, this.tomorrowsDate).pipe(catchError(_ => of(null)));

    this.subscriptions = this._coreProfileService.profile.pipe(
      combineLatestWith(
          connectedSourceChanged$, 
          profileStatus$, 
          shadowInfo$, 
          techSchedule$
        )
      ).subscribe(([userProfile, connected, profileStatus, shadowInfo, techSchedule]) => {
      this.online = connected || false;
      this.userProfile = userProfile;
      this.profileStatus = profileStatus;
      
      this.isShadowingUser = !!shadowInfo?.shadowing;

      const techCurrentSchedule: ScheduleByDate[] | any = techSchedule?.technicianSchedule?.scheduleByDate;
      if (techCurrentSchedule && techCurrentSchedule.length > 0 && techCurrentSchedule[0].available.length > 0 && techCurrentSchedule[0].available[0] && techCurrentSchedule[0].available[0].timeTo) {
        this.checkScheduleTime(techCurrentSchedule);
        this.intervalToCheckSchedule = setInterval(() => this.checkScheduleTime(techCurrentSchedule), 60000);
      }
      this.loading = false;
    });

    this.subscriptions.add(this._jobsService.jobs.subscribe((jobs) => {
      let jobsOpen = jobs?.filter(j => {
        return [JobStatus.PENDING,
        JobStatus.ALLOCATED,
        JobStatus.TECH_EN_ROUTE,
        JobStatus.TECH_ON_SITE].includes(lookupEnum(j.status.toUpperCase()))
      }).length ?? 0
      this.jobsLeft = `Pending Jobs: ${jobsOpen}`;
      
      const shownJobIds = this._jobsService.getShownJobIds();
      if(this.profileStatus?.vxFieldStatus === "LOGGED ON"){
        this.checkForIntrusivePromptOnJobAdded(jobs, shownJobIds);
      }
    }));
  }

  public checkForIntrusivePromptOnJobAdded(jobs: Job[], visitedJobIds: Set<string>): void {
    const pucJobs = jobs?.filter(job => job.isCallPuc);
    if(pucJobs && pucJobs?.length < 1){
      this._jobsService.clearShownJobIds();
    }
    
    if (pucJobs && pucJobs?.length > 0  && !this._jobsService.hasPopupBeenShown()) {

      if(this._coreCacheService.getLocalStorageItem('jobdisplay')?.value!=null) {
      this.jobDisplayStatus= JSON.parse(this._coreCacheService.getLocalStorageItem('jobdisplay')?.value);
      }
      let latestPucJob;
      if (pucJobs && pucJobs.length > 0) {
        latestPucJob = pucJobs[pucJobs.length - 1];
      }
      if (latestPucJob?.jobId  && this.jobDisplayStatus) {
            this.showIntrusivePrompt(latestPucJob);
            this._jobsService.setPopupShown(true);
            this._jobsService.addShownJobId(latestPucJob.jobId);
          }
      else {
        this._coreCacheService.setLocalStorageItem('jobdisplay', JSON.stringify(true));
        pucJobs.forEach(job => {
          if (job && job?.jobId && !visitedJobIds.has(job?.jobId)) {
            this._jobsService.addShownJobId(job.jobId);
          }
        });
        this.showPucNotification(jobs);
        this._jobsService.setPopupShown(true);
      }
    }
  }

  calculatePucCommitDate(reportedDate: string): Date {
    const reported = new Date(reportedDate);
    return new Date(reported.getTime() + 24 * 60 * 60 * 1000);
  }

  showIntrusivePrompt(job: Job): void {
    const dialogRef = this._dialog.open(IntrusivePromptDialog, {
      data: { job: job }
    });
    dialogRef.afterClosed().subscribe(result => {
      this._jobsService.setPopupShown(false);
  });
  }
  public showPucNotification(pucJobs: Job[]): void {
   const dialogRef = this._dialog.open(PucNotificationDialog, {
      data: { jobs: pucJobs }
    });
    dialogRef.afterClosed().subscribe(result => {
      this._jobsService.setPopupShown(false);
  });
  }

  checkScheduleTime(techCurrentSchedule: ScheduleByDate[] | any) {
    this.endingScheduleDate = new Date(techCurrentSchedule[0].available[0].timeTo);
    const currentTime = new Date();
    this.finishTime = differenceInMinutes(this.endingScheduleDate, currentTime);
    this.isScheduleDueIn30Minutes = this.finishTime <= 30 && this.finishTime >= 0;
    this.isScheduleDue = this.finishTime < 0;
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
    if (this.intervalToCheckSchedule) {
      clearInterval(this.intervalToCheckSchedule);
    }
  }

}
